import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { dashboardAPI, companiesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Wallet,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Loader2
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, companiesRes] = await Promise.all([
        dashboardAPI.getSummary(),
        companiesAPI.getAll()
      ]);
      setSummary(summaryRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const chartData = summary?.monthly_income?.map((income, index) => ({
    month: income.month?.slice(5) || '',
    income: income.amount,
    expense: summary?.monthly_expense?.[index]?.amount || 0
  })) || [];

  const pieData = [
    { name: 'Income', value: summary?.total_income || 0 },
    { name: 'Expense', value: summary?.total_expense || 0 }
  ];

  return (
    <Layout title="Dashboard">
      <div className="space-y-6" data-testid="dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <Card className="card-hover" data-testid="stat-companies">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Companies</p>
                  <p className="text-3xl font-heading font-bold mt-1">
                    {summary?.companies_count || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-personnel">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Personnel</p>
                  <p className="text-3xl font-heading font-bold mt-1">
                    {summary?.personnel_count || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-income">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Income</p>
                  <p className="text-2xl font-heading font-bold mt-1 text-emerald-500">
                    {formatCurrency(summary?.total_income || 0)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
              <div className="flex items-center mt-2 text-xs text-emerald-500">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                <span>All time</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-expense">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Expense</p>
                  <p className="text-2xl font-heading font-bold mt-1 text-red-500">
                    {formatCurrency(summary?.total_expense || 0)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-500" />
                </div>
              </div>
              <div className="flex items-center mt-2 text-xs text-red-500">
                <ArrowDownRight className="h-3 w-3 mr-1" />
                <span>All time</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Affiliate Metrics */}
        {summary?.affiliate && summary.affiliate.total_deposits > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Affiliate Overview</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <div className="p-3 rounded-lg bg-purple-500/5 text-center"><p className="text-[10px] text-muted-foreground">Clicks</p><p className="text-sm font-bold text-purple-500">{(summary.affiliate.total_clicks || 0).toLocaleString()}</p></div>
                <div className="p-3 rounded-lg bg-cyan-500/5 text-center"><p className="text-[10px] text-muted-foreground">Registrations</p><p className="text-sm font-bold text-cyan-500">{(summary.affiliate.total_registrations || 0).toLocaleString()}</p></div>
                <div className="p-3 rounded-lg bg-amber-500/5 text-center"><p className="text-[10px] text-muted-foreground">FTD</p><p className="text-sm font-bold text-amber-500">{(summary.affiliate.total_ftd || 0).toLocaleString()}</p></div>
                <div className="p-3 rounded-lg bg-blue-500/5 text-center"><p className="text-[10px] text-muted-foreground">Deposits</p><p className="text-sm font-bold text-blue-500">{formatCurrency(summary.affiliate.total_deposits || 0)}</p></div>
                <div className="p-3 rounded-lg bg-red-500/5 text-center"><p className="text-[10px] text-muted-foreground">Withdrawals</p><p className="text-sm font-bold text-red-500">{formatCurrency(summary.affiliate.total_withdrawals || 0)}</p></div>
                <div className={`p-3 rounded-lg text-center ${(summary.affiliate.total_net_result || 0) >= 0 ? 'bg-emerald-500/5' : 'bg-orange-500/5'}`}><p className="text-[10px] text-muted-foreground">Net Result</p><p className={`text-sm font-bold ${(summary.affiliate.total_net_result || 0) >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>{formatCurrency(summary.affiliate.total_net_result || 0)}</p></div>
                <div className="p-3 rounded-lg bg-emerald-500/5 text-center"><p className="text-[10px] text-muted-foreground">Commission</p><p className="text-sm font-bold text-emerald-500">{formatCurrency(summary.affiliate.total_commission || 0)}</p></div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Area Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading">Income / Expense Chart</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="month" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [formatCurrency(value), '']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="income" 
                        stroke="#10B981" 
                        fillOpacity={1} 
                        fill="url(#colorIncome)"
                        name="Income"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="expense" 
                        stroke="#EF4444" 
                        fillOpacity={1} 
                        fill="url(#colorExpense)"
                        name="Expense"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Net Balance */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Net Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center h-[250px]">
                <div className="relative">
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#10B981' : '#EF4444'} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Wallet className="h-5 w-5 text-muted-foreground mb-1" />
                    <span className={`text-lg font-heading font-bold ${(summary?.net_income || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(summary?.net_income || 0)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm text-muted-foreground">Income</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm text-muted-foreground">Expense</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Companies List */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">My Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {companies.length > 0 ? (
                  <div className="space-y-3">
                    {companies.map((company) => (
                      <div 
                        key={company.id}
                        className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{company.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {company.description || 'No description'}
                          </p>
                        </div>
                        <Badge variant={company.is_active ? 'default' : 'secondary'}>
                          {company.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Building2 className="h-12 w-12 mb-2 opacity-50" />
                    <p>No companies added yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Upcoming Payments */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Upcoming Payments</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {summary?.upcoming_payments?.length > 0 ? (
                  <div className="space-y-3">
                    {summary.upcoming_payments.map((payment, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{payment.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(payment.next_payment_date).toLocaleDateString('en-US')}
                          </p>
                        </div>
                        <span className="font-medium text-red-500">
                          {formatCurrency(payment.amount, payment.currency || 'TRY')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Calendar className="h-12 w-12 mb-2 opacity-50" />
                    <p>No upcoming payments</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
